import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ITrackerRecordState } from './tracker-record.state';
import {
  ISideEffectService,
  ISymptomService,
  SIDE_EFFECT_SERVICE,
  SYMPTOM_SERVICE,
} from '@mobile-data-access-services';
import {
  catchError,
  EMPTY,
  finalize,
  mergeMap,
  Observable,
  of,
  pipe,
  switchMap,
  take,
} from 'rxjs';
import {
  ScreenCodes,
  SpinnerContainerIds,
  TrackerEffectServeCodes
} from '@mobile-data-access-enums';
import {
  ISmartNavigatorService,
  ISpinnerService, NavigateToScreenRequest,
  SMART_NAVIGATOR_SERVICE,
  SPINNER_SERVICE
} from '@ui-tool/core';
import { TrackerNavigationRequest } from '@mobile-data-access-models';

@Injectable()
export class TrackerRecordComponentStore extends ComponentStore<ITrackerRecordState> {
  //#region Methods
  public readonly submit = this.effect<{
    onSubmittedAsyncHandler?: () => Observable<void>;
  }>(
    pipe(
      switchMap(({ onSubmittedAsyncHandler }) => {
        let displaySpinnerRequestId = this._spinnerService.displaySpinner(
          SpinnerContainerIds.APPLICATION
        );
        const ctcaeName = this.get().data?.title || null;
        const { selectedCode, ctcaeCode } = this.get();
        return this._symptomService
          .submitTrackingAsync(
            ctcaeCode as string,
            ctcaeName as string,
            selectedCode as TrackerEffectServeCodes
          )
          .pipe(
            mergeMap(() => {
              if (!onSubmittedAsyncHandler) {
                return of(void 0);
              }

              return onSubmittedAsyncHandler();
            }),
            catchError(() => EMPTY),
            finalize(() =>
              this._spinnerService.deleteSpinner(
                SpinnerContainerIds.APPLICATION,
                displaySpinnerRequestId
              )
            )
          );
      })
    )
  );

  public readonly redirect = this.effect<never>(
    pipe(
      switchMap(() => {
        return this.select((x) => ({
          ctCaeCode: x.ctcaeCode,
          selectedCode: x.selectedCode,
        })).pipe(take(1));
      }),
      mergeMap(({ selectedCode, ctCaeCode }) => {
        const severities = this.get().severityDefinitions || [];
        let severityCode = severities
          .find((x) => x.ctcaeCode === ctCaeCode)
          ?.settings?.find((x) => x.code === selectedCode)
          ?.behavior as TrackerEffectServeCodes | null;
        if (!severityCode) {
          severityCode = selectedCode;
        }

        if (severityCode === TrackerEffectServeCodes.G1) {
          const navigateRequest = new NavigateToScreenRequest(ScreenCodes.SYMPTOM_DETAIL, {id: ctCaeCode!}, { queryParams: { previous: 'tracker'} })
          return this._navigationService.navigateToScreenAsync(navigateRequest);
        }

        return this._navigationService.navigateToScreenAsync(
          new TrackerNavigationRequest()
        );
      })
    )
  );
  //#endregion

  //#region Constructor
  public constructor(
    @Inject(SYMPTOM_SERVICE)
    protected readonly _symptomService: ISymptomService,
    @Inject(SPINNER_SERVICE)
    protected readonly _spinnerService: ISpinnerService,
    @Inject(SMART_NAVIGATOR_SERVICE)
    protected readonly _navigationService: ISmartNavigatorService,
    @Inject(SIDE_EFFECT_SERVICE)
    protected readonly _sideEffectService: ISideEffectService
  ) {
    super({
      data: null,
      selectedCode: null,
      ctcaeCode: null,
      severityDefinitions: [],
    });
  }
  public readonly updateData = this.updater<ITrackerRecordState['data']>(
    (state, data) => {
      return {
      ...state,
      data
      };
    }
    );
  public readonly updateCtcaeCode = this.updater<
    ITrackerRecordState['ctcaeCode']
  >((state, ctcaeCode) => ({
    ...state,
    ctcaeCode,
  }));
  public readonly updateSelectedCode = this.updater<
    ITrackerRecordState['selectedCode']
  >((state, selectedCode) => ({
    ...state,
    selectedCode,
  }));
  public readonly updateSeverityDefinitions = this.updater<
    ITrackerRecordState['severityDefinitions']
  >((state, severityDefinitions) => ({
    ...state,
    severityDefinitions,
  }));

  //#endregion
}
